import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { toast } from 'react-toastify'
import {
	useUpdateBranchSpheraLocation,
	useUpdateCrSpheraService,
} from '@/api/queries/sphera-export-queries'
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '@/components/ui/accordion'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { ROUTES } from '@/constants/routes'
import type { SpheraOptionItem, SpheraPreviewOutput } from '@/types'
import { getApiErrorMessage } from '@/utils/api-error'

interface SpheraPendingPanelProps {
	pending: SpheraPreviewOutput['pending']
	services: SpheraOptionItem[]
}

function PendingCrRow({
	cr,
	services,
}: {
	cr: SpheraPreviewOutput['pending']['crs'][number]
	services: SpheraOptionItem[]
}) {
	const { t } = useTranslation()
	const [service, setService] = useState('')
	const mutation = useUpdateCrSpheraService()

	const save = async () => {
		try {
			await mutation.mutateAsync({ id: cr.id, spheraService: service })
			toast.success(t('administration.spheraExport.saved'))
		} catch (error) {
			toast.error(getApiErrorMessage(error))
		}
	}

	return (
		<li className="flex flex-col gap-2 py-2 sm:flex-row sm:items-center">
			<div className="min-w-0 flex-1">
				<p className="text-sm text-kaefer-dark-grey" title={cr.name}>
					<span className="font-semibold">{cr.code}</span> · {cr.name}
				</p>
				<p className="text-xs text-muted-foreground">{cr.branchName}</p>
			</div>
			<div className="sm:w-72 sm:shrink-0">
				<NativeSelect
					value={service}
					onChange={(e) => setService(e.target.value)}
				>
					<NativeSelectOption value="" disabled>
						{t('administration.spheraExport.selectService')}
					</NativeSelectOption>
					{services.map((item) => (
						<NativeSelectOption key={item.id} value={item.name}>
							{item.name}
						</NativeSelectOption>
					))}
				</NativeSelect>
			</div>
			<Button
				size="sm"
				disabled={!service || mutation.isPending}
				onClick={save}
			>
				{t('administration.spheraExport.saveButton')}
			</Button>
		</li>
	)
}

function PendingBranchRow({
	branch,
}: {
	branch: SpheraPreviewOutput['pending']['branches'][number]
}) {
	const { t } = useTranslation()
	const [location, setLocation] = useState('')
	const mutation = useUpdateBranchSpheraLocation()

	const save = async () => {
		try {
			await mutation.mutateAsync({
				id: branch.id,
				spheraLocation: location,
			})
			toast.success(t('administration.spheraExport.saved'))
		} catch (error) {
			toast.error(getApiErrorMessage(error))
		}
	}

	return (
		<li className="flex flex-col gap-2 py-2 sm:flex-row sm:items-center">
			<p className="min-w-0 flex-1 truncate text-sm text-kaefer-dark-grey">
				{branch.name}
			</p>
			<div className="sm:w-72 sm:shrink-0">
				<Input
					value={location}
					onChange={(e) => setLocation(e.target.value)}
					placeholder={t('administration.spheraExport.locationPlaceholder')}
				/>
			</div>
			<Button
				size="sm"
				disabled={!location.trim() || mutation.isPending}
				onClick={save}
			>
				{t('administration.spheraExport.saveButton')}
			</Button>
		</li>
	)
}

/** Itens do mês que ainda não têm de/para do Sphera: configurar aqui ou no cadastro de origem. */
export function SpheraPendingPanel({
	pending,
	services,
}: SpheraPendingPanelProps) {
	const { t } = useTranslation()
	const { crs, branches, catalog } = pending

	if (crs.length === 0 && branches.length === 0 && catalog.length === 0) {
		return null
	}

	const sectionTrigger =
		'py-2 text-xs font-semibold uppercase text-amber-900/80'

	return (
		<div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
			<p className="text-sm font-semibold text-amber-900">
				{t('administration.spheraExport.pendingTitle')}
			</p>

			<Accordion type="multiple">
				{crs.length > 0 && (
					<AccordionItem value="crs" className="border-amber-200">
						<AccordionTrigger className={sectionTrigger}>
							{t('administration.spheraExport.pendingCrs', {
								count: crs.length,
							})}
						</AccordionTrigger>
						<AccordionContent>
							<ul className="max-h-96 divide-y divide-amber-200 overflow-y-auto pr-1">
								{crs.map((cr) => (
									<PendingCrRow key={cr.id} cr={cr} services={services} />
								))}
							</ul>
						</AccordionContent>
					</AccordionItem>
				)}

				{branches.length > 0 && (
					<AccordionItem value="branches" className="border-amber-200">
						<AccordionTrigger className={sectionTrigger}>
							{t('administration.spheraExport.pendingBranches', {
								count: branches.length,
							})}
						</AccordionTrigger>
						<AccordionContent>
							<ul className="divide-y divide-amber-200">
								{branches.map((branch) => (
									<PendingBranchRow key={branch.id} branch={branch} />
								))}
							</ul>
						</AccordionContent>
					</AccordionItem>
				)}

				{catalog.length > 0 && (
					<AccordionItem value="catalog" className="border-amber-200">
						<AccordionTrigger className={sectionTrigger}>
							{t('administration.spheraExport.pendingCatalog')} (
							{catalog.length})
						</AccordionTrigger>
						<AccordionContent className="space-y-2">
							<ul className="list-disc pl-5 text-sm text-kaefer-dark-grey">
								{catalog.map((item) => (
									<li key={item.id}>
										{item.name} (
										{item.type === 'KIND'
											? t('administration.spheraExport.kind')
											: t('administration.spheraExport.classification')}
										)
									</li>
								))}
							</ul>
							<div className="flex gap-3 text-sm">
								<Link
									to={ROUTES.ADMINISTRATION.TYPOLOGY_RISKS}
									className="underline"
								>
									{t('administration.options.typologyRisks.title')}
								</Link>
								<Link
									to={ROUTES.ADMINISTRATION.CLASSIFICATIONS}
									className="underline"
								>
									{t('administration.options.classifications.title')}
								</Link>
							</div>
						</AccordionContent>
					</AccordionItem>
				)}
			</Accordion>
		</div>
	)
}
